import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthenticationService } from '../apps/Authentication/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  public readonly permissions$ = this.permissionsSubject.asObservable();

  constructor(private authService: AuthenticationService) {
    console.log('[PermissionsService] Inicializando servicio de permisos');
    
    // Suscribirse a cambios en el usuario para cargar permisos desde el usuario
    this.authService.currentUser$.subscribe((user) => {
      console.log('[PermissionsService] Usuario cambió, cargando permisos. Usuario:', user);
      this.loadPermissionsFromUser(user);
    });
    
    // Cargar permisos del usuario actual si ya está disponible
    const currentUser = this.authService.getCurrentUserSnapshot();
    if (currentUser) {
      this.loadPermissionsFromUser(currentUser);
    }
  }
  
  /**
   * Carga los permisos desde el objeto usuario (que viene de /api/auth/me)
   */
  private loadPermissionsFromUser(user: any | null): void {
    if (!user) {
      console.log('[PermissionsService] No hay usuario, limpiando permisos');
      this.permissionsSubject.next([]);
      return;
    }

    console.log('[PermissionsService] Cargando permisos desde usuario:', user);

    const permissions: string[] = [];

    // Buscar permisos en diferentes propiedades del usuario
    if (user.permissions && Array.isArray(user.permissions)) {
      console.log('[PermissionsService] Permisos encontrados en user.permissions:', user.permissions);
      permissions.push(...user.permissions.filter((p: any) => typeof p === 'string'));
    }

    if (user.Permissions && Array.isArray(user.Permissions)) {
      console.log('[PermissionsService] Permisos encontrados en user.Permissions:', user.Permissions);
      permissions.push(...user.Permissions.filter((p: any) => typeof p === 'string'));
    }

    // Si el usuario tiene roles, intentar obtener permisos de los roles
    // (esto requeriría una petición adicional al backend)
    if (user.roles && Array.isArray(user.roles) && permissions.length === 0) {
      console.log('[PermissionsService] Usuario tiene roles pero no permisos directos. Roles:', user.roles);
      // Los permisos deberían venir del backend en el objeto usuario
    }

    // Eliminar duplicados
    const uniquePermissions = Array.from(new Set(permissions.filter(p => p && typeof p === 'string')));
    
    console.log('[PermissionsService] ========== RESUMEN DE PERMISOS ==========');
    console.log('[PermissionsService] Permisos extraídos:', uniquePermissions);
    console.log('[PermissionsService] Total de permisos:', uniquePermissions.length);
    console.log('[PermissionsService] =========================================');
    
    if (uniquePermissions.length === 0) {
      console.warn('[PermissionsService] ⚠️ NO SE ENCONTRARON PERMISOS EN EL USUARIO');
      console.warn('[PermissionsService] El backend debe incluir los permisos en la respuesta de /api/auth/me');
    }
    
    this.permissionsSubject.next(uniquePermissions);
  }

  /**
   * Decodifica un JWT token (solo la parte del payload)
   */
  private decodeJwtToken(token: string): any {
    try {
      if (!token || typeof token !== 'string') {
        console.error('[PermissionsService] Token inválido:', token);
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('[PermissionsService] Token no tiene el formato correcto (debe tener 3 partes separadas por puntos)');
        return null;
      }

      const base64Url = parts[1];
      if (!base64Url) {
        console.error('[PermissionsService] No se pudo extraer el payload del token');
        return null;
      }

      // Reemplazar caracteres URL-safe
      let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      
      // Agregar padding si es necesario
      while (base64.length % 4) {
        base64 += '=';
      }

      // Decodificar base64
      const decoded = atob(base64);
      
      // Convertir a JSON de forma más segura
      try {
        return JSON.parse(decoded);
      } catch (parseError) {
        // Si falla el parse directo, intentar con decodeURIComponent
        try {
          const jsonPayload = decodeURIComponent(
            decoded
              .split('')
              .map(c => {
                const charCode = c.charCodeAt(0);
                if (charCode > 127) {
                  return '%' + ('00' + charCode.toString(16)).slice(-2);
                }
                return c;
              })
              .join('')
          );
          return JSON.parse(jsonPayload);
        } catch (uriError) {
          console.error('[PermissionsService] Error parseando JSON del token:', uriError);
          console.error('[PermissionsService] Contenido decodificado:', decoded);
          return null;
        }
      }
    } catch (error) {
      console.error('[PermissionsService] Error decodificando token:', error);
      console.error('[PermissionsService] Token recibido:', token?.substring(0, 50) + '...');
      return null;
    }
  }

  /**
   * Carga los permisos desde el token JWT actual
   */
  private loadPermissionsFromToken(): void {
    const token = this.authService.getToken();
    if (!token) {
      console.log('[PermissionsService] No hay token disponible');
      this.permissionsSubject.next([]);
      return;
    }

    console.log('[PermissionsService] Token obtenido, longitud:', token.length);
    console.log('[PermissionsService] Primeros 50 caracteres del token:', token.substring(0, 50));

    const decoded = this.decodeJwtToken(token);
    if (!decoded) {
      console.log('[PermissionsService] No se pudo decodificar el token');
      this.permissionsSubject.next([]);
      return;
    }

    console.log('[PermissionsService] Token decodificado exitosamente');
    
    // Log temporal para debug - mostrar todo el payload
    console.log('[PermissionsService] ========== TOKEN PAYLOAD COMPLETO ==========');
    console.log(JSON.stringify(decoded, null, 2));
    console.log('[PermissionsService] ============================================');

    console.log('[PermissionsService] Token decodificado completo:', JSON.stringify(decoded, null, 2));
    console.log('[PermissionsService] Claves del token:', Object.keys(decoded));
    
    // Mostrar todas las claves que contengan "permission" (case insensitive)
    const permissionKeys = Object.keys(decoded).filter(k => k.toLowerCase().includes('permission'));
    console.log('[PermissionsService] Claves relacionadas con Permission:', permissionKeys);

    // Extraer claims de tipo "Permission"
    // En JWT de .NET, cuando hay múltiples claims con el mismo tipo, se serializan como:
    // 1. Un array JSON: "Permission": ["perm1", "perm2"] (formato más común)
    // 2. Claims con namespace: "http://schemas.microsoft.com/ws/2008/06/identity/claims/permission"
    // 3. Como propiedades individuales (menos común)
    
    const permissions: string[] = [];
    
    // Primero, buscar directamente "Permission" (case sensitive, como lo envía .NET)
    if (decoded['Permission']) {
      const value = decoded['Permission'];
      console.log('[PermissionsService] Encontrado claim "Permission":', value, 'Tipo:', typeof value);
      if (Array.isArray(value)) {
        const arrayPerms = value.filter(v => typeof v === 'string');
        console.log(`[PermissionsService] Agregando ${arrayPerms.length} permisos del array:`, arrayPerms);
        permissions.push(...arrayPerms);
      } else if (typeof value === 'string') {
        console.log(`[PermissionsService] Agregando permiso único:`, value);
        permissions.push(value);
      }
    }
    
    // Buscar todas las propiedades del payload que contengan "Permission" (case insensitive)
    Object.keys(decoded).forEach(key => {
      const value = decoded[key];
      
      // Si la clave contiene "Permission" (case insensitive) y no es la que ya procesamos
      const keyLower = key.toLowerCase();
      if (keyLower.includes('permission') && key !== 'Permission') {
        console.log(`[PermissionsService] Encontrada clave relacionada con Permission: "${key}" =`, value, 'Tipo:', typeof value);
        
        if (Array.isArray(value)) {
          const arrayPerms = value.filter(v => typeof v === 'string');
          console.log(`[PermissionsService] Agregando ${arrayPerms.length} permisos del array:`, arrayPerms);
          permissions.push(...arrayPerms);
        } else if (typeof value === 'string') {
          console.log(`[PermissionsService] Agregando permiso único:`, value);
          permissions.push(value);
        } else if (value && typeof value === 'object') {
          console.log(`[PermissionsService] Valor es un objeto, intentando extraer:`, value);
          // Si es un objeto, intentar extraer valores
          if (Array.isArray(Object.values(value))) {
            const objPerms = Object.values(value).filter(v => typeof v === 'string') as string[];
            permissions.push(...objPerms);
          }
        }
      }
    });
    
    // Si aún no encontramos permisos, buscar en todas las claves que puedan contener arrays
    if (permissions.length === 0) {
      console.log('[PermissionsService] No se encontraron permisos con "Permission", buscando en todas las claves...');
      Object.keys(decoded).forEach(key => {
        const value = decoded[key];
        if (Array.isArray(value) && value.length > 0) {
          // Verificar si el array contiene strings que parezcan permisos (formato Resource.Action)
          const possiblePerms = value.filter(v => 
            typeof v === 'string' && 
            (v.includes('.') || v.match(/^[A-Z][a-zA-Z]*\.[A-Z][a-zA-Z]*$/))
          );
          if (possiblePerms.length > 0) {
            console.log(`[PermissionsService] Posibles permisos encontrados en clave "${key}":`, possiblePerms);
            permissions.push(...possiblePerms);
          }
        }
      });
    }

    // Si no encontramos permisos, buscar en otras variantes
    if (permissions.length === 0) {
      // Buscar en formato de claims agrupados
      if (decoded.claims && typeof decoded.claims === 'object') {
        const claims = decoded.claims as any;
        Object.keys(claims).forEach(claimKey => {
          if (claimKey.toLowerCase().includes('permission')) {
            const claimValue = claims[claimKey];
            if (Array.isArray(claimValue)) {
              permissions.push(...claimValue.filter((v: any) => typeof v === 'string'));
            } else if (typeof claimValue === 'string') {
              permissions.push(claimValue);
            }
          }
        });
      }
      
      // Buscar en formato de array de claims
      if (decoded.claim && Array.isArray(decoded.claim)) {
        decoded.claim.forEach((claim: any) => {
          if (claim && (claim.type === 'Permission' || claim.type?.toLowerCase().includes('permission')) && claim.value) {
            permissions.push(claim.value);
          }
        });
      }
    }

    // Eliminar duplicados
    const uniquePermissions = Array.from(new Set(permissions.filter(p => p && typeof p === 'string')));
    
    console.log('[PermissionsService] ========== RESUMEN DE PERMISOS ==========');
    console.log('[PermissionsService] Permisos extraídos:', uniquePermissions);
    console.log('[PermissionsService] Total de permisos:', uniquePermissions.length);
    console.log('[PermissionsService] =========================================');
    
    if (uniquePermissions.length === 0) {
      console.warn('[PermissionsService] ⚠️ NO SE ENCONTRARON PERMISOS EN EL TOKEN');
      console.warn('[PermissionsService] Esto puede significar que:');
      console.warn('[PermissionsService] 1. Los permisos no se están agregando al token en el backend');
      console.warn('[PermissionsService] 2. Los permisos están en un formato diferente al esperado');
      console.warn('[PermissionsService] 3. El usuario no tiene permisos asignados en sus roles');
    }
    
    this.permissionsSubject.next(uniquePermissions);
  }

  /**
   * Obtiene todos los permisos del usuario actual
   */
  getPermissions(): string[] {
    return this.permissionsSubject.value;
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    const permissions = this.getPermissions();
    return permissions.includes(permission);
  }

  /**
   * Verifica si el usuario tiene al menos uno de los permisos especificados
   */
  hasAnyPermission(permissions: string[]): boolean {
    if (!permissions || permissions.length === 0) {
      return true; // Si no se especifican permisos, permitir acceso
    }
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   */
  hasAllPermissions(permissions: string[]): boolean {
    if (!permissions || permissions.length === 0) {
      return true; // Si no se especifican permisos, permitir acceso
    }
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Recarga los permisos desde el usuario actual (útil después de actualizar roles)
   */
  reloadPermissions(): void {
    const currentUser = this.authService.getCurrentUserSnapshot();
    this.loadPermissionsFromUser(currentUser);
  }
}

